export type PlatformWorkbookBucketId =
    | 'identity_contact'
    | 'lease_preferences'
    | 'financial'
    | 'failed_qualification'
    | 'operational'
    | 'lifestyle'

/** Platform bucket id or custom_* section id. */
export type WorkbookBucketId = PlatformWorkbookBucketId | string

export type WorkbookSectionKind = 'platform' | 'custom'

export type CustomFieldInputType =
    | 'text'
    | 'textarea'
    | 'select'
    | 'boolean'
    | 'date'
    | 'number'
    | 'email'
    | 'phone'
    | 'address'

export interface WorkbookFieldConfig {
    field_id: string
    enabled: boolean
    required: boolean
    /** Catalog override or custom question prompt. */
    question_text?: string
    /** Custom fields only (also accepted as override label). */
    label?: string
    input_type?: string
    options?: string[]
    /** Identity gender prompt: ID marker vs self-ID. */
    gender_question_mode?: 'id_marker' | 'self_id'
    show_if?: {
        depends_on_field_id: string
        equals: string
    }
}

export interface WorkbookBucketConfig {
    bucket_id: WorkbookBucketId
    label: string
    summary?: string
    kind: WorkbookSectionKind
    enabled: boolean
    /** @deprecated Soft guidance only — no longer required to enable lifestyle. */
    lifestyle_acknowledged?: boolean
    fields: WorkbookFieldConfig[]
}

export interface WorkbookDefinition {
    version: number
    buckets: WorkbookBucketConfig[]
    /** Retained for progress helpers; not a hard placement constraint. */
    identity_phase_field_ids: string[]
}

export interface PropertyApplicationWorkbookListItem {
    id: number
    name: string
    apartment_id: number
    apartment_name?: string
    adoption_status: 'synced' | 'forked' | 'stale' | 'local'
    org_source_id?: number | null
    created_from_org_workbook_id?: number | null
    copied_from_id?: number | null
    copied_from_name?: string | null
    created_from_season_id?: number | null
    created_from_season_name?: string | null
    created_from_template_key?: string | null
    created_from_template_version?: number | null
    is_editable: boolean
    created_date: string
    modified_date: string
}

export interface PropertyApplicationWorkbookDetail
    extends PropertyApplicationWorkbookListItem {
    definition: WorkbookDefinition
    copied_from_id?: number | null
}

export interface OrgApplicationWorkbookListItem {
    id: number
    name: string
    created_from_template_key?: string | null
    created_from_template_version?: number | null
    is_editable: boolean
    created_date: string
    modified_date: string
}

export interface OrgApplicationWorkbookDetail
    extends OrgApplicationWorkbookListItem {
    definition: WorkbookDefinition
}

export interface ApplicationWorkbookTemplateListItem {
    key: string
    name: string
    description: string
    version: number
}

export interface ApplicationWorkbookTemplateDetail
    extends ApplicationWorkbookTemplateListItem {
    definition: WorkbookDefinition
    created_date: string
    modified_date: string
}

export interface EzosWorkbookField {
    field_id: string
    question_text: string
    input_type: string
    options: string[]
    default_section_id: string
    is_active: boolean
    /** Must remain placed in every workbook. Not the applicant-answer required flag. */
    pinned?: boolean
    created_date?: string
    modified_date?: string
}

/** Canonical EZOS starter template key (UI has no selector). */
export const STANDARD_WORKBOOK_TEMPLATE_KEY = 'standard_student'

export interface WorkbookGateConfig {
    screening_start: 'identity_complete' | 'workbook_complete' | 'manual_only'
    before_sign: 'workbook_complete' | 'identity_complete'
    remainder_due: 'before_sign' | 'before_lease' | 'after_lease' | 'never'
    pm_override: {
        allow_skip_to_screening: boolean
        allow_advance_incomplete: boolean
        require_reason: boolean
    }
}
